/**
 * Where the metronome's next clicks fall: the beats, and the subdivision clicks between them.
 *
 * Beat times advance a whole beat at a time, as they always have. A subdivision click is placed off
 * its own beat rather than added step after step, so no rounding builds up between beats.
 */

// Behind by more than this (a backgrounded tab) and the clicks restart at now instead of catching up
const SKIP_AHEAD = 0.1;

export const SUBDIVISIONS = [1, 2, 3, 4];

export const createCursor = (time) => ({ beatTime: time, beat: 1, step: 0, stepsInBeat: 1 });

/**
 * The clicks due before `now + lookAhead`, and the cursor to pick up from on the next tick.
 *
 * @returns {{ cursor: object, clicks: Array<{ time: number, beat: number, step: number }> }} Step 0 is
 *   the beat itself; later steps are its subdivision clicks.
 */
export const collectClicks = (cursor, { now, lookAhead, bpm, beatsPerMeasure, subdivision }) => {
  let { beatTime, beat, step, stepsInBeat } = cursor;
  const beatLength = 60 / bpm;
  const clicks = [];
  const stepTime = () => beatTime + (step * beatLength) / stepsInBeat;
  const nextBeatNumber = () => (beat >= beatsPerMeasure ? 1 : beat + 1);

  if (stepTime() < now - SKIP_AHEAD) {
    if (step > 0) beat = nextBeatNumber();

    beatTime = now;
    step = 0;
  }

  while (stepTime() < now + lookAhead) {
    // Settled as a beat starts, so a change mid-beat waits for the next one
    if (step === 0) stepsInBeat = subdivision;

    clicks.push({ time: stepTime(), beat, step });
    step += 1;

    if (step >= stepsInBeat) {
      step = 0;
      beat = nextBeatNumber();
      beatTime += beatLength;
    }
  }

  return { cursor: { beatTime, beat, step, stepsInBeat }, clicks };
};
